import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { FormattedMessage, useIntl } from 'react-intl';
import { Typography, Image } from '@alltrails/core';
import PlaceTrail from '@alltrails/denali/icons/PlaceTrail';
import BikingRoad from '@alltrails/denali/icons/BikingRoad';
import Hiking from '@alltrails/denali/icons/Hiking';
import BikingMtn from '@alltrails/denali/icons/BikingMtn';
import Message from '@alltrails/denali/icons/Message';
import First from '@alltrails/denali/icons/First';
import ImageIcon from '@alltrails/denali/icons/Image';
import Button from '@alltrails/denali/components/Button';
import Checkbox from '@alltrails/denali/components/Checkbox';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import Select from '@alltrails/denali/components/Select';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import TrailPartiallyPaved from '@alltrails/denali/icons/TrailPartiallyPaved';
import useActivityStrings from '@alltrails/shared/hooks/useActivityStrings';
import type { ActivityType } from '@alltrails/types/ActivityType';
import classNames from 'classnames';
import { useState, useMemo } from 'react';
import { BoundaryFilterType, StyleId } from '../../types/Styles';
import useStyleSelection from '../../hooks/useStyleSelection';
import { useDispatch, useSelector } from '../../redux';
import useStyleKeyboardShortcuts, { keycodeShortcuts } from '../../hooks/useStyleKeyboardShortcuts';
import { toggleHeatmapStyleId, updateAdminStyleSettings, addOrRemoveBoundaryFilter } from '../../redux/reducer';
import { baseStyleCardConfigs, staticStyleCardConfigs, dynamicStyleCardConfigs } from '../../utils/styleCardConfigs';
import { isBaseStyle, isStaticStyle, isDynamicStyle } from '../../utils/styleIdHelpers';
import adminUrlIsValid from '../../utils/adminUrlIsValid';
import styles from './ExtraMenuItem.module.scss';

const adminStyleSourceUrlId = 'adminOverlay';

type Props = {
  styleId: StyleId;
  onUrlButtonClick?: () => void;
} & Parameters<typeof useStyleSelection>[0];

const ExtraMenuItem = ({
  onUrlButtonClick,
  styleId,
  onMapBaseLayerTapped,
  onMapOverlayTapped,
  disableAnalytics,
  handleFailedEntitlementCheck,
  handleRoadblock
}: Props) => {
  const [boundaryTypeSelectOpen, setBoundaryTypeSelectOpen] = useState(false);
  const isMobile = useIsMobileSizedScreen();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { hasPermission } = useAuthorization();
  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const isPLP = hasPermission({ permission: 'public_lands:manage' });
  const { adminStyleSettings, baseStyleId, dynamicStyleIds, heatmapStyleIds, staticStyleIds, trailId } = useSelector(state => ({
    adminStyleSettings: state.map.adminStyleSettings,
    baseStyleId: state.map.baseStyleId,
    dynamicStyleIds: state.map.dynamicStyleIds,
    heatmapStyleIds: state.map.heatmapStyleIds,
    staticStyleIds: state.map.staticStyleIds,
    trailId: state.map.trailId
  }));

  const { onStyleSelect } = useStyleSelection({
    onMapBaseLayerTapped,
    onMapOverlayTapped,
    disableAnalytics,
    handleFailedEntitlementCheck,
    handleRoadblock
  });
  const handleStyleSelect = (styleId: StyleId) => {
    if (
      (isAdmin || isPLP) &&
      styleId === 'geoBoundaries' &&
      (!adminStyleSettings?.boundaryFilters || adminStyleSettings.boundaryFilters.length === 0)
    ) {
      dispatch(
        updateAdminStyleSettings({
          ...adminStyleSettings,
          boundaryFilters: [BoundaryFilterType.alertClosure, BoundaryFilterType.alertNonClosure]
        })
      );
    }
    onStyleSelect(styleId);
  };

  useStyleKeyboardShortcuts(handleStyleSelect);

  const { active, displayName, imgSrc } = useMemo(() => {
    if (isBaseStyle(styleId)) {
      const { displayName, imgSrc } = baseStyleCardConfigs[styleId];
      const active = baseStyleId === styleId;
      return { active, displayName, imgSrc };
    } else if (isStaticStyle(styleId)) {
      const { displayName, imgSrc } = staticStyleCardConfigs[styleId];
      const active = staticStyleIds.includes(styleId);
      return { active, displayName, imgSrc };
    } else {
      const { displayName, imgSrc } = dynamicStyleCardConfigs[styleId];
      let updatedDisplayName = displayName;
      if (styleId === 'heatmap') {
        // admins retain the ability to use "legacy" heatmaps in the "Extras" tab, with a new display name:
        if (trailId) {
          updatedDisplayName = intl.formatMessage({ defaultMessage: 'Attached recordings' });
        } else {
          updatedDisplayName = intl.formatMessage({ defaultMessage: 'Trail system' });
        }
      }
      const active = dynamicStyleIds.includes(styleId);
      return { active, displayName: updatedDisplayName, imgSrc };
    }
  }, [styleId, baseStyleId, staticStyleIds, dynamicStyleIds, trailId, intl]);

  const heatmapCheckbox = useMemo(() => {
    if (isDynamicStyle(styleId) && dynamicStyleCardConfigs[styleId].hasHeatmap && (isAdmin || isPLP)) {
      return (
        <Checkbox
          className={styles.heatmapCheckbox}
          disabled={!dynamicStyleIds.includes(styleId)}
          id={styleId}
          labelElement={<FormattedMessage defaultMessage="Include heatmap" key="checkbox-label-include-heatmap" />}
          onChange={() => dispatch(toggleHeatmapStyleId(styleId))}
          onClick={event => event.stopPropagation()}
          selected={heatmapStyleIds.includes(styleId)}
          testId={`${styleId}-heatmap`}
        />
      );
    }
    return null;
  }, [isAdmin, isPLP, dispatch, dynamicStyleIds, heatmapStyleIds, styleId]);

  const { activitySelectOptions } = useActivityStrings();

  const activitySelect = useMemo(() => {
    if (styleId === 'recordingsAll' || styleId === 'recordingsPop') {
      return (
        <Select
          className={styles.activitySelect}
          hideLabel
          labelText={intl.formatMessage({ defaultMessage: 'Activity type' })}
          options={[{ label: <FormattedMessage defaultMessage="All activity types" />, value: 'all' }, ...activitySelectOptions]}
          onChange={value =>
            dispatch(updateAdminStyleSettings({ ...adminStyleSettings, activityType: value === 'all' ? 'all' : (value as ActivityType) }))
          }
          onClick={e => e.stopPropagation()}
          placeholder=""
          size="sm"
          testId={`${styleId}-activity-select`}
          value={adminStyleSettings?.activityType}
        />
      );
    }
    return null;
  }, [activitySelectOptions, adminStyleSettings, dispatch, intl, styleId]);

  const boundaryAssignmentMultiselect = useMemo(() => {
    if (styleId === 'geoBoundaries') {
      const optionTitles = {
        [BoundaryFilterType.osm]: intl.formatMessage({ defaultMessage: 'OSM Parks' }),
        [BoundaryFilterType.alertClosure]: intl.formatMessage({ defaultMessage: 'Alerts: Closure' }),
        [BoundaryFilterType.alertNonClosure]: intl.formatMessage({ defaultMessage: 'Alerts: Danger, Caution, Info' }),
        [BoundaryFilterType.groupPermissions]: intl.formatMessage({ defaultMessage: 'Permissions' })
      };
      const filterTypes = isAdmin ? Object.values(BoundaryFilterType) : [BoundaryFilterType.alertClosure, BoundaryFilterType.alertNonClosure];
      const options = filterTypes.map(filter => ({
        selected: adminStyleSettings?.boundaryFilters?.includes(filter),
        testId: `option-${filter}`,
        title: optionTitles[filter],
        onClick: () => dispatch(addOrRemoveBoundaryFilter(filter))
      }));

      return (
        <DropdownMenu
          options={options}
          placement="bottom-start"
          anchor={
            <button
              className={styles.filterMultiSelect}
              data-testid="multiselect-style-filter"
              onClick={() => setBoundaryTypeSelectOpen(!boundaryTypeSelectOpen)}
            >
              <FormattedMessage
                defaultMessage="{filterCount, plural, =0 {All} one {# Filter} other {# Filters}}"
                values={{ filterCount: adminStyleSettings?.boundaryFilters?.length || 0 }}
              />
              <ChevronSm orientation={boundaryTypeSelectOpen ? 'up' : 'down'} size="sm" color="--color-text-placeholder" />
            </button>
          }
          isOpen={boundaryTypeSelectOpen}
          onCloseRequest={() => setBoundaryTypeSelectOpen(false)}
        />
      );
    }
    return null;
  }, [boundaryTypeSelectOpen, styleId, adminStyleSettings, dispatch, intl]);

  const keyboardShortcutText = useMemo(() => {
    const keycode = keycodeShortcuts[styleId];

    if (keycode && isAdmin) {
      return `Opt+${keycode.replace('Key', '')}`; // KeyA -> A
    }
    return undefined;
  }, [isAdmin, styleId]);

  const isDisabled = useMemo(() => {
    if (isAdmin) {
      if (styleId === adminStyleSourceUrlId && !adminUrlIsValid(adminStyleSettings?.styleSourceUrls?.[adminStyleSourceUrlId!])) {
        return true;
      }
    }
    if (styleId === 'surfaceType' && dynamicStyleIds.includes('groundConditions')) {
      // While groundConditions is applied, do not let users edit surfaceType
      return true;
    }
    return false;
  }, [adminStyleSettings?.styleSourceUrls, dynamicStyleIds, isAdmin, styleId]);

  const icon = useMemo(() => {
    if (styleId === 'surfaceType') {
      return <TrailPartiallyPaved />;
    }
    if (isAdmin) {
      return null;
    } else {
      if (styleId === 'waypoints') {
        return <Message />;
      } else if (styleId === 'photos') {
        return <ImageIcon />;
      } else if (styleId === 'distanceMarkers') {
        return <First />;
      } else if (styleId === 'geoBoundaries') {
        return <PlaceTrail />;
      } else if (styleId === 'nearbyTrails') {
        return <PlaceTrail />;
      } else if (styleId === 'waymarkedCycling') {
        return <BikingRoad />;
      } else if (styleId === 'waymarkedHiking') {
        return <Hiking />;
      } else if (styleId === 'waymarkedMTB') {
        return <BikingMtn />;
      } else {
        return null;
      }
    }
  }, [styleId, isAdmin]);

  const urlButton = useMemo(() => {
    const requiresUrl = styleId === adminStyleSourceUrlId;
    const hasCustomOverlayUrl = !!adminStyleSettings?.styleSourceUrls?.[adminStyleSourceUrlId!];

    if (requiresUrl && onUrlButtonClick) {
      return (
        <Button
          className={styles.urlButton}
          onClick={onUrlButtonClick}
          size="sm"
          stopPropagation
          testId={`${styleId}-url-button`}
          text={
            hasCustomOverlayUrl ? <FormattedMessage defaultMessage="Edit custom overlay" /> : <FormattedMessage defaultMessage="Add custom overlay" />
          }
        />
      );
    }
    return null;
  }, [adminStyleSettings?.styleSourceUrls, onUrlButtonClick, styleId]);

  return (
    <div className={classNames(styles.container, { [styles.adminContainer]: isAdmin })}>
      <div className={styles.containerInner}>
        <div className={styles.iconBackground}>
          {icon ?? <Image className={styles.image} alt={intl.formatMessage({ defaultMessage: 'Preview' })} src={imgSrc} width={48} height={48} />}
        </div>
        <Checkbox
          className={styles.selectCheckbox}
          disabled={isDisabled}
          id={`${styleId}-select-layer`}
          labelElement={
            <div
              className={classNames(styles.textContainer, {
                [styles.disabled]: isDisabled
              })}
              key={`${styleId}-checkbox-label`}
            >
              <Typography
                variant="text200"
                className={classNames(styles.displayName, {
                  [styles.disabled]: isDisabled
                })}
              >
                {displayName}
              </Typography>
              {!isMobile && keyboardShortcutText && (
                <Typography variant="text100" color="secondary">
                  {keyboardShortcutText}
                </Typography>
              )}
            </div>
          }
          onChange={() => handleStyleSelect(styleId)}
          onClick={event => event.stopPropagation()}
          selected={active}
          testId={`${styleId}-checkbox`}
          inputLast
        />
      </div>
      <div className={styles.adminControls}>
        {activitySelect}
        {boundaryAssignmentMultiselect}
        {heatmapCheckbox}
        {urlButton}
      </div>
    </div>
  );
};

export default ExtraMenuItem;
